import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Check, ChevronRight, X } from "lucide-react";
import api from "@/lib/api";

export default function WhatsNewModal({ open, onOpenChange }) {
  const [entries, setEntries] = useState([]);
  const [hasUnseen, setHasUnseen] = useState(false);
  const [lastSeenId, setLastSeenId] = useState(null);

  useEffect(() => {
    if (open) {
      fetchChangelog();
    }
  }, [open]);

  const fetchChangelog = async () => {
    try {
      const { data } = await api.get("/changelog");
      setEntries(data.entries || []);
      setHasUnseen(data.has_unseen);
      setLastSeenId(data.last_seen_id);
    } catch {
      // silent
    }
  };

  const markSeen = async () => {
    try {
      await api.post("/changelog/seen");
      setHasUnseen(false);
      onOpenChange(false);
    } catch {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden bg-background border-border" data-testid="whats-new-modal">
        <DialogTitle className="sr-only">What&apos;s New</DialogTitle>
        <div className="px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-5 w-5 text-brand-500" />
            <h2 className="text-lg font-bold">What&apos;s New</h2>
          </div>
          <p className="text-sm text-muted-foreground">Latest updates and features</p>
        </div>
        <ScrollArea className="max-h-[420px]">
          <div className="px-6 py-4 space-y-5">
            {entries.map((entry, i) => {
              const isNew = lastSeenId ? entry.id > lastSeenId : i === 0;
              return (
                <div key={entry.id} className="relative" data-testid={`changelog-entry-${entry.id}`}>
                  {i > 0 && <div className="absolute -top-3 left-0 right-0 border-t border-border" />}
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant={isNew ? "default" : "secondary"} className={isNew ? "bg-brand-500/20 text-brand-400 border-brand-500/30" : ""}>
                      v{entry.version}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{entry.date}</span>
                    {isNew && hasUnseen && <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px]">NEW</Badge>}
                  </div>
                  <h3 className="font-semibold text-sm mb-2">{entry.title}</h3>
                  <ul className="space-y-1">
                    {entry.highlights.map((h, j) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Check className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </ScrollArea>
        <div className="px-6 py-4 border-t border-border flex justify-end">
          <Button onClick={markSeen} size="sm" data-testid="whats-new-dismiss">
            Got it <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hook to check for unseen changelog
export function useWhatsNew() {
  const [hasUnseen, setHasUnseen] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/changelog");
        setHasUnseen(data.has_unseen);
      } catch {
        // silent
      }
    };
    check();
  }, []);

  return { hasUnseen, setHasUnseen };
}
